import { DatePipe, Location } from "@angular/common";
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";

import { ActivatedRoute, Router } from "@angular/router";
import { QueryParams } from "@ngrx/data";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { BsModalService } from "ngx-bootstrap/modal";
import { OrderPipe } from "ngx-order-pipe";
import { SelectItem } from "primeng/api/selectitem";
import { combineLatest, fromEvent, Observable, Subscription } from "rxjs/index";
import { debounceTime, filter, map, tap } from "rxjs/operators";
import { AuthenticationSelectors } from "../../auth/state/authentication.selectors";
import { Provider } from "../../core/model/central/provider.model";
import { MessageDisplayDialogComponent } from "../../message-list/message-display-dialog.component";
import { AppointmentSettingsSelectors } from "../appointment-settings/state/appointment-settings.selectors";
import { Appointment } from "../appointments/state/appointment.model";
import { DateAppointmentService } from "../appointments/state/date-appointment.service";
import { PatientAppointmentService } from "../patients/state/patient-appointment.service";
import { PatientsSelectors } from "../patients/state/patients.selectors";
import { ProviderService } from "../reminder-settings/state/provider.service";
import { TriggerOffset } from "../reminder-settings/strategies/trigger-offset.model";
import { AppointmentListActions } from "./state/appointment-list.actions";
import { AppointmentListSelectors } from "./state/appointment-list.selectors";
import { AppointmentListService } from "./state/appointment-list.service";
import { RemindersReportService } from "./state/reminders-report.service";
import { PreviewEditTemplateSelectors } from '../../preview-edit-template/state/preview-edit-template.selectors';

@Component({
  selector: "ng-appointment-list",
  templateUrl: "./appointment-list.component.html",
  styleUrls: ["./appointment-list.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class AppointmentListComponent
  implements OnInit, OnDestroy, AfterViewInit, OnChanges, AfterViewChecked
{
  @Input() appointments: Array<Appointment>;
  @Input() readonly loading: boolean;
  @Output() readonly loadDataOnScroll = new EventEmitter();
  @ViewChild("appointmentListScrollContainer")
  appointmentListScrollContainer: ElementRef;
  @Input() readonly urlType;
  @Input() readonly strategies;
  @Input() readonly queryFilter;
  @Input() readonly selectedRemoteAppointmentId;
  @Input() readonly selectedAppointmentId;
  @Input() readonly currentUser;
  @Input() readonly viewSessionId;
  @Input() readonly selectedDate;
  @Input() readonly selectedFromDate;
  @Input() readonly selectedToDate;

  viewModel$: Observable<any>;
  isAuthenticatedFromAdmin$: Observable<boolean>;
  providers$: Observable<Array<Provider>>;
  viewDeletedReminders$: Observable<boolean>;
  appointmentStates$: Observable<Array<any>>;
  displayBirthday$: Observable<boolean>;
  displayTwoway$: Observable<boolean>;
  displayIncorrectDestinator$: Observable<boolean>;
  remindersLoading$: Observable<boolean>;
  viewVoidAppointments$: Observable<boolean>;
  showProviders$: Observable<boolean>;
  locations$: Observable<Array<string>>;

  reminderColspan: 5; // needed for UI to display reminders expand correctly
  selectedAppointmentStatus: Array<any> = [];
  appointmentStatus: Array<SelectItem> = [];
  reminderStates: Array<SelectItem>;
  providers: Array<SelectItem> = [];
  selectedReminderStates: Array<any> = [];
  selectedProviders: Array<any> = [];
  selectedLocations: Array<string> = [];
  subscriptions: Subscription = new Subscription();
  remindersLoading = false;

  expandedRows: {} = {}; // to open reminder report when remoteAppointmentId is provided
  locationsItem: Array<SelectItem> = [];
  selectedAppointment: Appointment;
  pinnedAppointments = [];
  public isCollapsed = true;
  pipe = new DatePipe("en-US");
  fromDate: Date;
  offset = 0;
  openedAppointments = []; // contain open appointments on UI
  showBubble = false;
  hoverAppointmentId: any;
  hoverGroupMasterAppointmentId: any;
  isReminderRequest = false;
  patientDateRange: Array<Date>;
  shouldRemove = false;
  triggerOffset = new TriggerOffset(undefined);

  @ViewChild("appointmentListTable") appointmentListTable: any;
  isAppointmentTableLoaded = false;

  constructor(
    private appointmentsListSelectors: AppointmentListSelectors,
    private authenticationSelectors: AuthenticationSelectors,
    private providerService: ProviderService,
    private patientsSelectors: PatientsSelectors,
    private modalService: BsModalService,
    private appointmentSettingsSelector: AppointmentSettingsSelectors,
    private appointmentListService: AppointmentListService,
    private dateAppointmentService: DateAppointmentService,
    private patientAppointmentService: PatientAppointmentService,
    private translate: TranslateService,
    private remindersReportService: RemindersReportService,
    private orderPipe: OrderPipe,
    private store: Store<any>,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private previewEditTemplateSelectors: PreviewEditTemplateSelectors
  ) {
    this.viewDeletedReminders$ =
      this.appointmentsListSelectors.selectViewDeletedReminder();

    this.viewVoidAppointments$ =
      this.appointmentsListSelectors.selectViewVoidAppointments();

    this.isAuthenticatedFromAdmin$ =
      this.authenticationSelectors.selectIsAuthenticatedFromAdmin();
    this.providers$ = this.providerService.entities$;
    this.displayBirthday$ =
      this.appointmentSettingsSelector.selectDisplayBirthday();
    this.displayTwoway$ =
      this.appointmentSettingsSelector.selectDisplayTwoway();
    this.displayIncorrectDestinator$ =
      this.appointmentSettingsSelector.selectDisplayIncorrectDestinator();
    this.showProviders$ =
      this.appointmentSettingsSelector.selectShowProviders();
    this.remindersLoading$ =
      this.remindersReportService.refreshing$ ||
      this.remindersReportService.loading$;
    this.subscriptions.add(
      fromEvent(window, "resize")
        .pipe(debounceTime(100))
        .subscribe((evt) => this.onScrollDown(evt))
    );
    this.locations$ = this.previewEditTemplateSelectors.selectGetLocations();
    const filters: QueryParams = {
      appointmentId: "0",
      viewSessionId: this.viewSessionId,
    };
    this.remindersReportService.subscribeWebSocketJob(filters);
  }

  ngOnInit(): void {
    this.setReminderStates();

    if (this.currentUser !== undefined) {
      this.appointmentStates$ = this.appointmentListService.getAppointmentState(
        this.currentUser.userId
      );

      const subscribe = this.providerService.loaded$
        .pipe(filter((loaded) => !loaded))
        .subscribe((loaded) => this.providerService.getAll());

      this.subscriptions.add(subscribe);
    }

      this.locations$
          .pipe(
              filter(locations => locations !== undefined && locations !== null && locations.length > 0)
          )
          .subscribe(locations => {
                      this.locationsItem = [];
                      this.selectedLocations = [];
                      locations.forEach((location) => {
                          this.locationsItem.push({label: location, value: location});
                          this.selectedLocations.push(location);
                      });
                  }
            );

    this.fromDate = new Date(this.selectedDate);
    this.patientDateRange = [
      new Date(this.selectedFromDate),
      new Date(this.selectedToDate),
    ];

    this.viewModel$ = combineLatest([
      this.isAuthenticatedFromAdmin$,
      this.providers$,
      this.displayBirthday$,
      this.displayTwoway$,
      this.displayIncorrectDestinator$,
      this.viewDeletedReminders$,
      this.remindersLoading$,
      this.viewVoidAppointments$,
      this.showProviders$,
      this.locations$
    ]).pipe(
      tap(
        ([
          isAuthenticatedFromAdmin,
          providers,
          displayBirthday,
          displayTwoway,
          displayIncorrectDestinator,
          viewDeletedReminders,
          remindersLoading,
          viewVoidAppointments,
          showProviders,
          locations,
        ]) =>
          console.log({
            isAuthenticatedFromAdmin,
            providers,
            displayBirthday,
            displayTwoway,
            displayIncorrectDestinator,
            viewDeletedReminders,
            remindersLoading,
            viewVoidAppointments,
            showProviders,
            locations,
          })
      ),
      map(
        ([
          isAuthenticatedFromAdmin,
          providers,
          displayBirthday,
          displayTwoway,
          displayIncorrectDestinator,
          viewDeletedReminders,
          remindersLoading,
          viewVoidAppointments,
          showProviders,
          locations,
        ]) => {
          this.reminderColspan = 5;

          const selectFromProviders = providers as Array<any>;

          if (selectFromProviders.length > 0 && showProviders) {
            this.reminderColspan += 1;
          }

          if (
            locations !== undefined &&
            locations !== null &&
            showProviders
          ) {
            this.reminderColspan += 1;
          }

          return {
            isAuthenticatedFromAdmin,
            providers,
            displayBirthday,
            displayTwoway,
            displayIncorrectDestinator,
            viewDeletedReminders,
            remindersLoading,
            viewVoidAppointments,
            showProviders,
            locations
          };
        }
      )
    );
  }

  getAppointmentByDate(event): void {
    if (this.shouldRemove) {
      this.removeAppointmentIdFromUrl();
    }

    const mySimpleFormat =
      event !== undefined
        ? this.pipe.transform(event, "yyyy-MM-dd")
        : undefined;
    if (
      event !== undefined &&
      event !== null &&
      mySimpleFormat !== undefined &&
      this.queryFilter !== undefined &&
      this.queryFilter.date !== mySimpleFormat
    ) {
      this.isReminderRequest = false;

      this.dateAppointmentService.clearCache();
      const filters: QueryParams = {
        requestType: "appointmentByDate",
        date: mySimpleFormat.toString(),
        offset: "0",
        viewSessionId: this.viewSessionId,
      };
      this.dateAppointmentService.resetWebSocketJob(filters);
      this.appointmentListService.resetWebSocketJob({ personIds: [] });
      this.remindersReportService.clearCache();
      this.openedAppointments = [];
      this.pinnedAppointments = [];
      this.expandedRows = {};

      const reminderfilters: QueryParams = {
        appointmentId: "0",
        viewSessionId: this.viewSessionId,
      };
      this.remindersReportService.refreshWebSocketJob(reminderfilters);
      sessionStorage.setItem("appointmentByDate", mySimpleFormat.toString());
    }
  }

  toggle(datePicker): any {
    this.shouldRemove = true;
    datePicker.toggle();
  }

  removeAppointmentIdFromUrl(): any {
    if (this.shouldRemove) {
      const snapshot = this.route.snapshot;
      const params: any = { ...snapshot.queryParams };
      this.selectedAppointment = undefined;

      if (params.appointmentid !== undefined) {
        delete params.appointmentid;
      }
      // NOTE: Since we just added appointmentid in URL not in queryparam for patient, we have to remove it from location
      const currentURLWithParam = this.router.createUrlTree(["."], {
        relativeTo: this.route,
        queryParams: params,
      });
      this.location.go(currentURLWithParam.toString());
    }
  }

  getAppointmentByPatient(event): void {
    if (this.shouldRemove) {
      this.removeAppointmentIdFromUrl();
    }

    const selectedFrom =
      event !== undefined && event !== null
        ? this.pipe.transform(event[0], "yyyy-MM-dd")
        : undefined;

    if (
      event !== undefined &&
      event !== null &&
      selectedFrom !== undefined &&
      selectedFrom !== null &&
      this.queryFilter !== undefined
    ) {
      this.isReminderRequest = false;

      const selectedTo = this.pipe.transform(event[1], "yyyy-MM-dd");

      if (
        selectedFrom === this.queryFilter.fromDate &&
        selectedTo === this.queryFilter.toDate
      ) {
        console.log("Same date selected");
      } else {
        this.patientAppointmentService.clearCache();
        const filters: QueryParams = {
          requestType: "appointmentByPatientId",
          fromDate: selectedFrom.toString(),
          toDate: selectedTo.toString(),
          personId: this.queryFilter.personId.toString(),
          offset: "0",
          viewSessionId: this.viewSessionId,
        };
        this.patientAppointmentService.resetWebSocketJob(filters);
        this.appointmentListService.resetWebSocketJob({ personIds: [] });
        this.remindersReportService.clearCache();
        this.openedAppointments = [];
        this.pinnedAppointments = [];
        this.expandedRows = {};

        const reminderfilters: QueryParams = {
          appointmentId: "0",
          viewSessionId: this.viewSessionId,
        };
        this.remindersReportService.refreshWebSocketJob(reminderfilters);
        const dateRange = {
          fromDate: selectedFrom.toString(),
          toDate: selectedTo.toString(),
        };
        sessionStorage.setItem(
          "appointmentByPatient",
          JSON.stringify(dateRange)
        );
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.remindersReportService.unsubscribeWebSocketJob();
  }

  showAppointmentLog(appointment, dialogType, selectedMessage): void {
    // On clicking bubble, the data row is not getting selected
    this.selectedAppointment = appointment;
    this.store.dispatch(
      AppointmentListActions.openAppointmentLogsFromAppointmentList({
        appointment,
        dialogType,
        selectedMessage,
        userTimeZone: this.currentUser.userTimeZone,
        userPermissionKeys: this.currentUser.userPermissionKeys,
      })
    );
  }

  pinAppointment(appointmentId): void {
    if (!this.isPinedAppointment(appointmentId)) {
      this.pinnedAppointments.push(appointmentId);
    } else {
      this.unpinAppointment(appointmentId);
    }
  }

  closeAppointment(appointmentId): void {
    this.unpinAppointment(appointmentId);
    const index = this.openedAppointments.findIndex(
      (openedAppointment) => openedAppointment === appointmentId
    );
    this.openedAppointments.splice(index, 1);

    const filters: QueryParams = {
      appointmentId: "0",
      appointmentIds: { ...this.openedAppointments },
      viewSessionId: this.viewSessionId,
    };
    this.remindersReportService.refreshWebSocketJob(filters);
  }

  unpinAppointment(appointmentId): void {
    if (this.isPinedAppointment(appointmentId)) {
      const index = this.pinnedAppointments.findIndex(
        (pinnedAppointment) => pinnedAppointment === appointmentId
      );
      this.pinnedAppointments.splice(index, 1);
    }
  }

  isPinedAppointment(appointmentId): boolean {
    return (
      this.pinnedAppointments.findIndex(
        (pinnedAppointment) => pinnedAppointment === appointmentId
      ) > -1
    );
  }

  previewByTrackingCode(
    trackingCode,
    mediaType,
    messageUuid,
    messageType
  ): any {
    const initialState = {
      messageUuid: trackingCode === undefined ? messageUuid : trackingCode,
      mediaType,
      isTrackingcode: trackingCode !== undefined,
      messageType,
    };

    // Uncomment to force an SMS message
    // initialState.messageUuid = 'f373b428-d305-11ea-886c-0026b937cf5e';
    // initialState.mediaType = 'sms';

    this.modalService.show(MessageDisplayDialogComponent, {
      initialState,
      backdrop: "static",
      class: "modal-lg",
      keyboard: false, // disable esc key
    });
  }

  getColorByStatus(status): any {
    switch (status) {
      case "":
        return { "background-color": "" };

      case "confirmed": // confirmed
        return { "background-color": "#03de03" };

      case "failed":
      case "voiceincomplete":
        return { "background-color": "#e20404" };

      default:
        return { "background-color": "#ffbc00" };
    }
  }

  getAppointmentDetails(appointment): void {
    console.log("getAppointmentDetails :-- Get Reminders details");
    const snapshot = this.route.snapshot;
    const params: any = { ...snapshot.queryParams };
    params.appointmentid = appointment.appointmentId;

    // NOTE: on Patient Page when clicking Reminder report first time was causing reload if using navigate due to adding additional param.
    const currentURLWithParam = this.router.createUrlTree(["."], {
      relativeTo: this.route,
      queryParams: params,
    });
    this.location.go(currentURLWithParam.toString());
    this.remindersLoading = true;
    this.selectedAppointment = appointment;

    // If not open - open and add to list
    const index = this.openedAppointments.findIndex(
      (openAppointment) => openAppointment === appointment.appointmentId
    );
    if (index === -1) {
      this.openedAppointments.push(appointment.appointmentId);

      if (
        this.urlType === "appointment" &&
        appointment.groupMasterId !== undefined &&
        appointment.groupMasterId !== null &&
        appointment.groupMasterId !== appointment.appointmentId
      ) {
        this.openedAppointments.push(appointment.groupMasterId);
      }
      const filters: QueryParams = {
        appointmentId: appointment.appointmentId.toString(),
        appointmentIds: { ...this.openedAppointments },
        viewSessionId: this.viewSessionId,
      };
      this.remindersReportService.refreshWebSocketJob(filters);
    }

    this.expandedRows = {};
    // Close all unpinned appointments which are not closed
    const openAppointmentList = [];
    this.openedAppointments.forEach((openAppointment) =>
      openAppointmentList.push(openAppointment)
    );

    openAppointmentList.forEach((openedAppointmentId) => {
      if (
        this.isPinedAppointment(openedAppointmentId) ||
        (openedAppointmentId === appointment.appointmentId && index === -1)
      ) {
        this.expandedRows[openedAppointmentId] = true;
        if (
          openedAppointmentId === appointment.appointmentId &&
          this.urlType === "appointment"
        ) {
          this.expandedRows[appointment.groupMasterId] = true;
        }
      } else {
        this.closeAppointment(openedAppointmentId);
      }
    });
  }

  isOpenedAppointment(appointmentId): string {
    const tooltip =
      this.openedAppointments.indexOf(appointmentId) === -1
        ? "Show a detailed reminder report"
        : "Close Reminder Report";

    return tooltip;
  }

  getOffsetText(templateId, offsetFixed, offsetRelative, offsetText): string {
    if (templateId === undefined) {
      return `${offsetText || ""}`;
    }

    if (offsetFixed === 0 && offsetRelative === 0) {
      return "Send upon appointment creation (asap)";
    }
    this.triggerOffset.offsetsToUIModel(offsetRelative, offsetFixed);

    return this.triggerOffset.label();
  }

  setReminderStates(): void {
    // Create list of available states to select in the dropdown
    this.reminderStates = [
      {
        label: "No reminders scheduled",
        value: "",
      },
      {
        label: "Reminders scheduled",
        value: "registered",
      },
      {
        label: "Reminders in progress",
        value: "sent",
      },
      {
        label: "Failed to convey appointment details",
        value: "failed",
      },
      {
        label: "Failed to convey appointment details ( Voice )",
        value: "voiceincomplete",
      },
      {
        label: "Delivered in voice mail",
        value: "deliveredvoicemail",
      },
      {
        label: "Delivered, no confirmation requested",
        value: "delivered",
      },
      {
        label: "Delivered. Confirmation pending",
        value: "confirmpending",
      },
      {
        label: "Appointment is confirmed",
        value: "confirmed",
      },
    ];
    // TODO: Get from store - and use NgRx Effect to get/set in local storage?
    // Initially select all
    this.reminderStates.forEach((reminderStatus) =>
      this.selectedReminderStates.push(reminderStatus.value)
    );
  }

  isSelectedGroup(groupMasterId): boolean {
    return (
      this.selectedAppointment !== undefined &&
      this.selectedAppointment !== null &&
      this.selectedAppointment.groupMasterId !== undefined &&
      this.selectedAppointment.groupMasterId !== null &&
      this.selectedAppointment.groupMasterId === groupMasterId
    );
  }

  isHoveredGroup(appointment): boolean {
    return (
      (this.hoverAppointmentId !== undefined &&
        this.hoverAppointmentId !== null &&
        appointment.groupMasterId !== undefined &&
        appointment.groupMasterId !== null &&
        appointment.appointmentId === this.hoverAppointmentId) ||
      (this.hoverGroupMasterAppointmentId !== undefined &&
        this.hoverGroupMasterAppointmentId === appointment.appointmentId) ||
      this.hoverAppointmentId === appointment.groupMasterId
    );
  }

  ngAfterViewInit(): void {
    this.subscriptions.add(
      this.providers$
        .pipe(
          filter((providers) => providers !== undefined && providers.length > 0)
        )
        .subscribe((providers) => {
          if (providers.length > 0) {
            this.providers = [];
            this.selectedProviders = [];

            providers.forEach((provider) => {
              if (
                this.providers.findIndex(
                  (providerItem) => providerItem.value === provider.providerName
                ) === -1
              ) {
                this.providers.push({
                  label: provider.providerName,
                  value: provider.providerName,
                });
                this.selectedProviders.push(provider.providerName);
              }
            });
          }
        })
    );

    this.subscriptions.add(
      this.appointmentStates$
        .pipe(
          filter(
            (appointmentStates) =>
              appointmentStates !== undefined && appointmentStates.length > 0
          )
        )
        .subscribe((appointmentStates) => {
          if (appointmentStates.length > 0) {
            this.appointmentStatus = [];
            this.selectedAppointmentStatus = [];
            appointmentStates.forEach((appointmentState) => {
              if (
                appointmentState.remoteAppointmentState.toLowerCase() !== "void"
              ) {
                this.appointmentStatus.push({
                  label: appointmentState.remoteAppointmentState,
                  value: appointmentState.remoteAppointmentState,
                });

                this.selectedAppointmentStatus.push(
                  appointmentState.remoteAppointmentState
                );
              }
            });
          }
        })
    );
  }

  getMediaType(destinatorType, mediaType): string {
    return mediaType === "email" ? mediaType : destinatorType;
  }

  onScrollDown(event): void {
    console.log("scrolled DOWN!!");
    if (!this.loading) {
      // TODO: Consider setting debounce on this event?
      this.loadDataOnScroll.emit();
      this.isReminderRequest = false;
    }
  }

  displayStrategyName(strategyId): string {
    if (strategyId !== null) {
      const selectedStrategy = this.strategies.find(
        (strategy) => strategy.strategyId === strategyId
      );

      return selectedStrategy !== undefined
        ? selectedStrategy.strategyName
        : "Strategy was deleted";
    }

    return "No Strategy";
  }

  strategyDeleted(strategyId): boolean {
    return (
      this.strategies.find((strategy) => strategy.strategyId === strategyId) ===
      undefined
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.appointments !== undefined &&
      changes.appointments.previousValue !== undefined && // todo: find a better way to call this only once
      changes.appointments.previousValue.length === 0
    ) {
      // Checking previous value so its done only once
      let appointment;
      if (
        this.selectedRemoteAppointmentId !== undefined &&
        this.selectedRemoteAppointmentId !== null &&
        this.appointments.length > 0
      ) {
        appointment = this.appointments.find(
          (selectedAppointment) =>
            selectedAppointment.remoteAppointmentId.toString() ===
            this.selectedRemoteAppointmentId
        );
        this.selectAndDisplayAppointment(appointment);
      } else if (
        this.selectedAppointmentId !== undefined &&
        this.selectedAppointmentId !== null &&
        this.appointments.length > 0
      ) {
        appointment = this.appointments.find(
          (selectedAppointment) =>
            selectedAppointment.appointmentId.toString() ===
            this.selectedAppointmentId
        );
        this.selectAndDisplayAppointment(appointment);
      }
    }

    if (changes.appointments !== undefined && this.appointments.length > 0) {
      const personIds: Array<string> = [];
      this.appointments.forEach((appointment) => {
        if (
          personIds.find((personid) => +personid === appointment.personId) ===
          undefined
        ) {
          personIds.push(appointment.personId.toString());
        }
      });
      this.appointmentListService.resetWebSocketJob({ personIds });
    }

    if (changes.appointments !== undefined && this.appointments.length === 0) {
      this.isReminderRequest = false;
    }

    if (
      changes.showProviders !== undefined &&
      !changes.showProviders.isFirstChange()
    ) {
      this.onProviderChange(
        this.selectedProviders,
        changes.showProviders.currentValue
      );
    }
  }

  selectAndDisplayAppointment(appointment): void {
    if (appointment !== undefined) {
      this.isReminderRequest = true;
      // when refresh button is clicked, close all opened , pinned appointments and get the details for
      // the one whose appointment id is in url
      this.pinnedAppointments = [];
      const openAppointmentList = [];
      this.openedAppointments.forEach((openAppointment) =>
        openAppointmentList.push(openAppointment)
      );
      openAppointmentList.forEach((openedAppointmentId) => {
        this.closeAppointment(openedAppointmentId);
      });

      this.expandedRows[appointment.appointmentId] = true;
      this.fromDate = new Date(appointment.appointmentTimeMs);
      this.getAppointmentDetails(appointment);
    }
  }

  getMediaTypes(mediaTypes: any): Array<string> {
    const mediaTypeList: Array<any> = [];
    if (mediaTypes !== undefined && mediaTypes !== null) {
      mediaTypes.forEach((unavailableMedia) => {
        if (unavailableMedia.destinatorText.includes("phone")) {
          const alreadyIncludedPhoneMediaType =
            mediaTypeList.find((media) =>
              media.destinatorText.includes("phone")
            ) || false;
          if (!alreadyIncludedPhoneMediaType) {
            mediaTypeList.push(unavailableMedia);
          }
        } else {
          mediaTypeList.push(unavailableMedia);
        }
      });
    }

    return mediaTypeList;
  }

  getDestinatorsForToolTip(mediaTypes, mediaType): string {
    let mediaTypeText = "";
    if (mediaType === "email") {
      mediaTypeText = this.translate.instant(`_destinatorType_${mediaType}_`);
    } else {
      mediaTypes.forEach((unavailableMedia) => {
        if (unavailableMedia.mediaType !== "email") {
          mediaTypeText = `${this.translate.instant(
            `_destinatorType_${unavailableMedia.destinatorText}_`
          )}, ${mediaTypeText}`;
        }
      });
    }

    return mediaTypeText;
  }

  getUserRouteQueryParams(param: string, userId: number): Object {
    return { [param]: userId };
  }

  onProviderChange(value, showProviders): any {
    if (showProviders && this.providers.length > 0) {
      return this.appointmentListTable.filter(value, "providerName", "in");
    }

    if (!showProviders) {
      return this.appointmentListTable.filter([], "providerName", "in");
    }
  }

  onLocationsChange(value, showProviders, locations): any {
    if (showProviders && locations.length > 0) {
      return this.appointmentListTable.filter(value, "location", "in");
    }

    if (!showProviders) {
      return this.appointmentListTable.filter([], "location", "in");
    }
  }

    onReminderStatesChange(value): any {
      return this.appointmentListTable.filter(value, "reminderState", "in");
    }

  ngAfterViewChecked(): any {
    if (
      this.appointmentListTable !== undefined &&
      !this.isAppointmentTableLoaded
    ) {
      this.isAppointmentTableLoaded = true;
      this.showProviders$
        .pipe(filter((showProvider) => showProvider))
        .subscribe((showProvider) => {
          this.onProviderChange(this.selectedProviders, showProvider);
          // this.onLocationsChange(this.selectedLocations, showProvider);
        })
        .unsubscribe();
    }
  }

  //  needed for patient  page to get take it as selected patient and get the date from storage rather than current date
  setPatientId(patientId): void {
    sessionStorage.setItem("previousSelectedPatient", patientId);

    const mySimpleFormat = sessionStorage.getItem("appointmentByDate");
    const getDateRange =
      sessionStorage.getItem("appointmentByPatient") !== null
        ? JSON.parse(sessionStorage.getItem("appointmentByPatient"))
        : { fromDate: new Date().toString(), toDate: new Date().toString() };
    if (getDateRange !== undefined) {
      const fromDate =
        new Date(mySimpleFormat) < new Date(getDateRange.fromDate)
          ? mySimpleFormat
          : getDateRange.fromDate;
      const toDate =
        new Date(mySimpleFormat) > new Date(getDateRange.toDate)
          ? mySimpleFormat
          : getDateRange.toDate;
      const dateRange = {
        fromDate: fromDate.toString(),
        toDate: toDate.toString(),
      };
      sessionStorage.setItem("appointmentByPatient", JSON.stringify(dateRange));
    }
  }

  selectedDeselectedAppointmentStatus(): void {
    if (this.selectedAppointmentStatus.length > 0) {
      this.selectedAppointmentStatus = [];
    } else {
      this.selectedAppointmentStatus = [];
      this.appointmentStatus.forEach((appointmentStatus) => {
        this.selectedAppointmentStatus.push(appointmentStatus.label);
      });
    }
  }

  selectedDeselectedProviders(): void {
    if (this.selectedProviders.length > 0) {
      this.selectedProviders = [];
    } else {
      this.selectedProviders = [];
      this.providers.forEach((provider) => {
        this.selectedProviders.push(provider.label);
      });
    }
  }

  selectedDeselectedLocation(): void {
    if (this.selectedLocations.length > 0) {
      this.selectedLocations = [];
    } else {
      this.selectedLocations = [];
      this.locationsItem.forEach((location) => {
        this.selectedLocations.push(location.label);
      });
    }
  }

  selectedDeselectedReminderState(): void {
    if (this.selectedReminderStates.length > 0) {
      this.selectedReminderStates = [];
    } else {
      this.selectedReminderStates = [];
      this.reminderStates.forEach((reminderState) => {
        this.selectedReminderStates.push(reminderState.value);
      });
    }
  }
}
